import React, { useRef, useState } from 'react';
import {
  Table,
  TableProps,
  Layout,
  Input,
  Select,
  Tag,
  Button,
  Space,
} from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { useQuery } from 'react-query';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';

import { handleAPIError, IFetchQuery } from '@services/base';
import {
  getCarrierLogs,
  ICarrierLog,
  ICarrierLogFilter,
} from '@services/carrierLog';
import LogDetailDrawer from './components/LogDetailDrawer';

const { Content } = Layout;

const PAGE_SIZE = 50;

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const Widget: React.FC = () => {
  const refContainer = useRef<HTMLDivElement>(null);

  const [filter, setFilter] = useState<ICarrierLogFilter>({
    carrier_id: '',
    action: '',
    target_id: '',
    success: '',
  });
  const [query, setQuery] = useState<IFetchQuery>({
    page: 1,
    page_size: PAGE_SIZE,
  });

  const [data, setData] = useState<ICarrierLog[]>([]);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<any>({
    current: 1,
    total: 0,
    pageSize: PAGE_SIZE,
    showSizeChanger: false,
    position: ['bottomCenter'],
  });

  const { isFetching, refetch } = useQuery(
    ['carrier-logs', query, filter],
    () => getCarrierLogs({ ...query, ...filter }),
    {
      keepPreviousData: true,
      onSuccess: res => {
        setData(res?.logs || []);
        setPagination((prev: any) => ({
          ...prev,
          total: res?.total_values || 0,
        }));
      },
      onError: handleAPIError,
    },
  );

  const columns: ColumnsType<ICarrierLog> = [
    {
      title: 'Date',
      key: 'created_at',
      width: 180,
      render: (log: ICarrierLog) => formatDateTime(log.createdAt),
    },
    {
      title: 'Carrier',
      key: 'carrier',
      width: 200,
      render: (log: ICarrierLog) => log.carrier_name || log.carrier_id || '-',
    },
    {
      title: 'Action',
      key: 'action',
      dataIndex: 'action',
      width: 180,
      render: (action?: string) => action || '-',
    },
    {
      title: 'Target',
      key: 'target_id',
      width: 200,
      render: (log: ICarrierLog) => log.target_id || '-',
    },
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (log: ICarrierLog) => log.email || log.carrier_name || '-',
    },
    {
      title: 'Success',
      key: 'success',
      width: 100,
      render: (log: ICarrierLog) => (
        <Tag color={log.success ? 'green' : 'red'}>
          {log.success ? 'Success' : 'Failed'}
        </Tag>
      ),
    },
  ];

  const onChangeTable: TableProps<ICarrierLog>['onChange'] = pag => {
    if (pag.current !== pagination.current) {
      setQuery(prev => ({ ...prev, page: pag.current, page_size: PAGE_SIZE }));
      setPagination((prev: any) => ({ ...prev, current: pag.current }));
    }
  };

  const onChangeFilter = (key: keyof ICarrierLogFilter, value: string) => {
    setFilter(prev => ({ ...prev, [key]: value }));
    setQuery(prev => ({ ...prev, page: 1 }));
    setPagination((prev: any) => ({ ...prev, current: 1 }));
  };

  return (
    <>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Input
          placeholder="Carrier ID"
          allowClear
          prefix={<SearchOutlined />}
          value={filter.carrier_id}
          onChange={e => onChangeFilter('carrier_id', e.target.value)}
          style={{ width: 200 }}
        />
        <Input
          placeholder="Action"
          allowClear
          value={filter.action}
          onChange={e => onChangeFilter('action', e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          placeholder="Target ID"
          allowClear
          value={filter.target_id}
          onChange={e => onChangeFilter('target_id', e.target.value)}
          style={{ width: 200 }}
        />
        <Select
          placeholder="Success"
          allowClear
          value={filter.success || undefined}
          onChange={value => onChangeFilter('success', value || '')}
          style={{ width: 140 }}
          options={[
            { label: 'Success', value: 'true' },
            { label: 'Failed', value: 'false' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Space>

      <Content
        className={'bg-white'}
        style={{ borderRadius: '15px', overflow: 'hidden', padding: '5px' }}
      >
        <div ref={refContainer} style={{ height: '96%' }}>
          <Table
            loading={isFetching}
            onChange={onChangeTable}
            columns={columns}
            pagination={pagination}
            rowKey="_id"
            dataSource={data}
            onRow={record => ({
              onClick: () => setSelectedLogId(record._id),
              style: { cursor: 'pointer' },
            })}
            scroll={
              refContainer?.current
                ? {
                    y: refContainer.current.clientHeight - 56 - 56,
                    x: refContainer.current.clientWidth,
                  }
                : undefined
            }
          />
        </div>
      </Content>

      <LogDetailDrawer
        logId={selectedLogId}
        onClose={() => setSelectedLogId(null)}
      />
    </>
  );
};

export default Widget;
